import type { CustomNode, NodeStruct } from '@/types/node-types';
import type { CustomEdge } from '@/types/edge-types';
import type { JsonValue } from './functional-control-flow';

/**
 * The Jev Classifier node (`jev_classifier_node`): the editor's view of its
 * unchanged backend contract.
 *
 * Labels and yes/no questions are stored as the backend reads them, one
 * `name: text` line per entry (or a `{name: text}` object); this module turns
 * that into chips and rows and back, mirrors the backend's validation
 * messages, reads a run's result, and describes the plan in words.
 */

export const JEV_CLASSIFIER_NODE_ID = 'jev_classifier_node';

/** Same rule as the backend and Classify / Decide: a label names a Switch case. */
export const JEV_LABEL_NAME_RE = /^[a-z][a-z0-9_]{0,63}$/;
export const JEV_MAX_LABELS = 255;
export const JEV_DEFAULT_UNCERTAIN_LABEL = 'uncertain';
export const JEV_DEFAULT_UNCERTAIN_THRESHOLD = 0.6;
export const JEV_DEFAULT_LABEL_INSTRUCTIONS =
  'Which label best describes the item?';
export const JEV_DEFAULT_MAX_CONCURRENCY = 8;
export const JEV_MAX_CONCURRENCY = 32;

/** The params the Jev panel owns (everything the generic renderer would show
 * for them is rendered by the panel instead). `max_concurrency` is an
 * advanced param, which this line's sidebar never renders; the panel offers
 * it under "More settings". */
export const JEV_PANEL_FIELD_IDS: ReadonlySet<string> = new Set([
  'item',
  'labels',
  'label_instructions',
  'yes_no_questions',
  'uncertain_label',
  'uncertain_threshold',
  'max_concurrency',
]);

export interface JevLabel {
  name: string;
  criteria: string;
}

export interface JevQuestion {
  name: string;
  question: string;
}

export interface JevConfig {
  item: unknown;
  context: unknown;
  labels: JevLabel[];
  questions: JevQuestion[];
  labelInstructions: string;
  uncertainLabel: string;
  uncertainThreshold: number;
  maxConcurrency: number;
}

export interface JevConfigError {
  /** `labels`, `labels[2]`, `yes_no_questions[0]`, `uncertain_label`, `uncertain_threshold` */
  path: string;
  message: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

// --- `name: text` lines ------------------------------------------------------

/** Mirrors the backend's `_split_line`: strip a list marker, split on the first colon. */
const splitLine = (line: string): [string, string] => {
  let text = line.trim();
  if (text.startsWith('- ') || text.startsWith('* '))
    text = text.slice(2).trim();
  const colon = text.indexOf(':');
  if (colon === -1) return [text, ''];
  return [text.slice(0, colon).trim(), text.slice(colon + 1).trim()];
};

/** `(name, text)` pairs from `name: text` lines or a `{name: text}` object. */
export const parseNamedLines = (value: unknown): Array<[string, string]> => {
  if (value === null || value === undefined) return [];
  if (typeof value === 'string') {
    return value
      .split(/\r?\n/)
      .filter((line) => line.trim().length > 0)
      .map(splitLine);
  }
  if (isRecord(value)) {
    return Object.entries(value).map(([name, text]) => [
      name.trim(),
      text === null || text === undefined ? '' : String(text).trim(),
    ]);
  }
  return [];
};

export const parseLabels = (value: unknown): JevLabel[] =>
  parseNamedLines(value)
    .filter(([name]) => name.length > 0)
    .map(([name, criteria]) => ({ name, criteria }));

/** One `name: criteria` line per label; a label with no criteria is its name alone. */
export const formatLabels = (labels: readonly JevLabel[]): string =>
  labels
    .map(({ name, criteria }) =>
      criteria.trim() ? `${name.trim()}: ${criteria.trim()}` : name.trim(),
    )
    .join('\n');

/** The backend uses the question as the name (and vice versa) when one is missing. */
export const parseQuestions = (value: unknown): JevQuestion[] =>
  parseNamedLines(value)
    .map(([name, question]) => ({
      name: name || question,
      question: question || name,
    }))
    .filter((entry) => entry.name.length > 0);

export const formatQuestions = (questions: readonly JevQuestion[]): string =>
  questions
    .map(({ name, question }) =>
      question.trim() && question.trim() !== name.trim()
        ? `${name.trim()}: ${question.trim()}`
        : name.trim(),
    )
    .join('\n');

// --- config -----------------------------------------------------------------

const paramValue = (struct: NodeStruct, fieldId: string): unknown =>
  struct.params?.find((param) => param.field_id === fieldId)?.value;

const asNumber = (value: unknown, fallback: number): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
};

export const getJevConfig = (struct: NodeStruct): JevConfig => {
  const uncertain = paramValue(struct, 'uncertain_label');
  const instructions = paramValue(struct, 'label_instructions');
  return {
    item: paramValue(struct, 'item'),
    context: paramValue(struct, 'context'),
    labels: parseLabels(paramValue(struct, 'labels')),
    questions: parseQuestions(paramValue(struct, 'yes_no_questions')),
    labelInstructions:
      typeof instructions === 'string' && instructions.trim()
        ? instructions
        : JEV_DEFAULT_LABEL_INSTRUCTIONS,
    uncertainLabel:
      typeof uncertain === 'string' && uncertain.trim()
        ? uncertain.trim()
        : JEV_DEFAULT_UNCERTAIN_LABEL,
    uncertainThreshold: asNumber(
      paramValue(struct, 'uncertain_threshold'),
      JEV_DEFAULT_UNCERTAIN_THRESHOLD,
    ),
    maxConcurrency: asNumber(
      paramValue(struct, 'max_concurrency'),
      JEV_DEFAULT_MAX_CONCURRENCY,
    ),
  };
};

/** The labels Jev is offered: every label except the uncertain one. */
export const offeredLabels = (config: JevConfig): JevLabel[] =>
  config.labels.filter((label) => label.name !== config.uncertainLabel);

export const labelNameMessage = (name: string, what = 'Label'): string =>
  `${what} '${name}' must be lower_snake_case: a lowercase letter, then lowercase letters, digits or underscores, at most 64 characters.`;

/** The backend's checks, in its words, so the editor refuses what a run would refuse. */
export const validateJevConfig = (config: JevConfig): JevConfigError[] => {
  const errors: JevConfigError[] = [];
  const seen = new Set<string>();
  config.labels.forEach((label, index) => {
    if (!JEV_LABEL_NAME_RE.test(label.name)) {
      errors.push({
        path: `labels[${index}]`,
        message: labelNameMessage(label.name),
      });
    } else if (seen.has(label.name)) {
      errors.push({
        path: `labels[${index}]`,
        message: `Label '${label.name}' is listed more than once.`,
      });
    }
    seen.add(label.name);
  });
  if (!JEV_LABEL_NAME_RE.test(config.uncertainLabel)) {
    errors.push({
      path: 'uncertain_label',
      message: labelNameMessage(config.uncertainLabel, 'Uncertain label'),
    });
  }
  const offered = offeredLabels(config);
  if (offered.length === 0 && config.questions.length === 0) {
    errors.push({
      path: 'labels',
      message: 'Add labels or at least one yes/no question.',
    });
  } else if (offered.length === 1) {
    errors.push({
      path: 'labels',
      message: 'Add at least two labels to classify between.',
    });
  } else if (offered.length > JEV_MAX_LABELS) {
    errors.push({
      path: 'labels',
      message: `Jev accepts at most ${JEV_MAX_LABELS} labels; got ${offered.length}.`,
    });
  }
  const names = new Set<string>();
  config.questions.forEach((question, index) => {
    if (names.has(question.name)) {
      errors.push({
        path: `yes_no_questions[${index}]`,
        message: `Yes/no question '${question.name}' is listed more than once.`,
      });
    }
    names.add(question.name);
  });
  if (
    !Number.isFinite(config.uncertainThreshold) ||
    config.uncertainThreshold < 0 ||
    config.uncertainThreshold > 1
  ) {
    errors.push({
      path: 'uncertain_threshold',
      message: 'The uncertain threshold must be between 0 and 1.',
    });
  }
  return errors;
};

// --- catalog: model and cost ------------------------------------------------

export interface JevCatalogInfo {
  /** e.g. "jev-latest"; null when the catalog does not say. */
  model: string | null;
  provider: string;
  costUnit: string;
  costPerItem: number;
  costNote: string | null;
  resultKeys: string[];
}

export const getJevCatalogInfo = (struct: NodeStruct): JevCatalogInfo => {
  const contract = isRecord(struct.output_contract)
    ? struct.output_contract
    : {};
  const model = isRecord(contract.model) ? contract.model : {};
  const cost = isRecord(contract.cost) ? contract.cost : {};
  return {
    model: typeof model.requested === 'string' ? model.requested : null,
    provider:
      typeof model.provider === 'string' && typeof model.name === 'string'
        ? `${model.provider} ${model.name}`
        : "TypeSafe's Jev",
    costUnit: typeof cost.unit === 'string' ? cost.unit : 'requests',
    costPerItem: typeof cost.per_item === 'number' ? cost.per_item : 1,
    costNote: typeof cost.note === 'string' ? cost.note : null,
    resultKeys: Array.isArray(contract.result_keys)
      ? contract.result_keys.filter(
          (key): key is string => typeof key === 'string',
        )
      : [],
  };
};

// --- words ------------------------------------------------------------------

export const formatPercent = (
  probability: number | null | undefined,
): string =>
  typeof probability === 'number' && Number.isFinite(probability)
    ? `${Math.round(probability * 100)}%`
    : '—';

const joinNames = (names: string[]): string => {
  if (names.length <= 1) return names.join('');
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

/** "Jev will choose between billing, technical and other, answer 1 yes/no
 * question, and answer uncertain when the top probability is below 60%." */
export const describeJevPlan = (config: JevConfig): string => {
  const offered = offeredLabels(config).map((label) => label.name);
  const parts: string[] = [];
  if (offered.length >= 2) {
    parts.push(`choose between ${joinNames(offered)}`);
  }
  if (config.questions.length > 0) {
    parts.push(
      `answer ${config.questions.length} yes/no question${config.questions.length === 1 ? '' : 's'}`,
    );
  }
  if (parts.length === 0) {
    return 'Add at least two labels, or a yes/no question, and Jev will have something to decide.';
  }
  let sentence = `Jev will ${joinNames(parts)}`;
  if (offered.length >= 2) {
    sentence +=
      config.uncertainThreshold > 0
        ? `, and answer "${config.uncertainLabel}" when the top label's probability is below ${formatPercent(config.uncertainThreshold)}`
        : ', and always answer the top label';
  }
  return `${sentence}.`;
};

// --- results ----------------------------------------------------------------

export interface JevClassification {
  label: string | null;
  topLabel: string | null;
  topProbability: number | null;
  labelProbabilities: Record<string, number>;
  yesNo: Record<string, number>;
  uncertain: boolean;
  renormalised: boolean;
  model: string | null;
}

export interface JevBatchRow extends JevClassification {
  index: number;
  item: unknown;
  error: string | null;
}

export type JevRunSummary =
  | { kind: 'single'; classification: JevClassification }
  | {
      kind: 'batch';
      rows: JevBatchRow[];
      count: number;
      succeeded: number;
      failed: number;
      /** label → number of items, most frequent first */
      labelCounts: Array<[string, number]>;
      hasSpreadsheet: boolean;
    };

const numberMap = (value: unknown): Record<string, number> => {
  if (!isRecord(value)) return {};
  const out: Record<string, number> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (typeof entry === 'number' && Number.isFinite(entry)) out[key] = entry;
  }
  return out;
};

const readClassification = (
  value: Record<string, unknown>,
): JevClassification => ({
  label: typeof value.label === 'string' ? value.label : null,
  topLabel: typeof value.top_label === 'string' ? value.top_label : null,
  topProbability:
    typeof value.top_probability === 'number' ? value.top_probability : null,
  labelProbabilities: numberMap(value.label_probabilities),
  yesNo: numberMap(value.yes_no),
  uncertain: value.uncertain === true,
  renormalised: value.renormalised === true,
  model: typeof value.model === 'string' ? value.model : null,
});

const looksLikeClassification = (
  value: unknown,
): value is Record<string, unknown> =>
  isRecord(value) && 'label' in value && 'label_probabilities' in value;

const unwrapEnvelope = (value: unknown): unknown =>
  isRecord(value) && value._node_output === true ? value.data : value;

/**
 * What a run produced, read from the node's stored `result` (the shape the
 * runtime persists: `data` is the classification or the batch rows). `null`
 * when the node has not run, was skipped, or failed before producing one.
 */
export const readJevResult = (result: unknown): JevRunSummary | null => {
  if (!isRecord(result)) return null;
  if (result.status === 'skipped') return null;
  const data = unwrapEnvelope('data' in result ? result.data : result.value);
  if (looksLikeClassification(data)) {
    return { kind: 'single', classification: readClassification(data) };
  }
  if (Array.isArray(data) && data.length > 0 && data.every(isRecord)) {
    const rows: JevBatchRow[] = data.map((row, position) => ({
      ...readClassification(row),
      index: typeof row.index === 'number' ? row.index : position,
      item: row.item,
      error: typeof row.error === 'string' ? row.error : null,
    }));
    const counts = new Map<string, number>();
    for (const row of rows) {
      if (row.label) counts.set(row.label, (counts.get(row.label) ?? 0) + 1);
    }
    const files = Array.isArray(result.files) ? result.files : [];
    const failed = rows.filter((row) => row.error !== null).length;
    return {
      kind: 'batch',
      rows,
      count: rows.length,
      succeeded: rows.length - failed,
      failed,
      labelCounts: [...counts.entries()].sort((a, b) => b[1] - a[1]),
      hasSpreadsheet: files.length > 0,
    };
  }
  return null;
};

/** One short line for the node body: "billing · 91%", "uncertain (top: billing 52%)", "12 items · 11 classified, 1 failed". */
export const describeJevOutcome = (summary: JevRunSummary): string => {
  if (summary.kind === 'single') {
    const { label, topLabel, topProbability, uncertain } =
      summary.classification;
    if (uncertain) {
      return `${label ?? 'uncertain'} (top: ${topLabel ?? '—'} ${formatPercent(topProbability)})`;
    }
    return `${label ?? '—'} · ${formatPercent(topProbability)}`;
  }
  const tail = summary.failed
    ? `${summary.succeeded} classified, ${summary.failed} failed`
    : `${summary.succeeded} classified`;
  return `${summary.count} item${summary.count === 1 ? '' : 's'} · ${tail}`;
};

/** Label probabilities, highest first, as [label, probability]. */
export const rankedProbabilities = (
  classification: JevClassification,
): Array<[string, number]> =>
  Object.entries(classification.labelProbabilities).sort((a, b) => b[1] - a[1]);

// --- upstream sample ---------------------------------------------------------

export interface BoundParamSample {
  value: JsonValue;
  sourceName: string;
}

/**
 * The last value the upstream node sent into one of this node's bindable
 * params (the Jev panel offers it as the Item sample). `undefined` when the
 * param is not connected or the upstream node has no run yet.
 */
export const resolveBoundParamSample = (
  node: CustomNode,
  nodes: readonly CustomNode[],
  edges: readonly CustomEdge[],
  fieldId: string,
): BoundParamSample | undefined => {
  const portId = node.data.struct.params?.find(
    (param) => param.field_id === fieldId,
  )?.bindable_config?.input_port?.id;
  if (!portId) return undefined;
  const incoming = edges.find(
    (edge) => edge.target === node.id && edge.targetHandle === portId,
  );
  if (!incoming) return undefined;
  const source = nodes.find((candidate) => candidate.id === incoming.source);
  const result = source?.data?.result as Record<string, unknown> | undefined;
  if (!isRecord(result) || result.status === 'skipped') return undefined;
  const sourceName =
    source?.data?.struct?.user_config_name ||
    (source?.data as { display_name?: string } | undefined)?.display_name ||
    source?.data?.struct?.display_name ||
    'the upstream node';
  const portsData = isRecord(result.output_ports_data)
    ? result.output_ports_data
    : null;
  if (
    portsData &&
    incoming.sourceHandle &&
    incoming.sourceHandle in portsData
  ) {
    return {
      value: unwrapEnvelope(portsData[incoming.sourceHandle]) as JsonValue,
      sourceName,
    };
  }
  if ('data' in result) {
    return { value: unwrapEnvelope(result.data) as JsonValue, sourceName };
  }
  return undefined;
};

/** Name of the node connected to a bindable param, or null. */
export const boundParamSourceName = (
  node: CustomNode,
  nodes: readonly CustomNode[],
  edges: readonly CustomEdge[],
  fieldId: string,
): string | null => {
  const portId = node.data.struct.params?.find(
    (param) => param.field_id === fieldId,
  )?.bindable_config?.input_port?.id;
  if (!portId) return null;
  const incoming = edges.find(
    (edge) => edge.target === node.id && edge.targetHandle === portId,
  );
  if (!incoming) return null;
  const source = nodes.find((candidate) => candidate.id === incoming.source);
  return (
    source?.data?.struct?.user_config_name ||
    (source?.data as { display_name?: string } | undefined)?.display_name ||
    source?.data?.struct?.display_name ||
    null
  );
};

/** A sample value shown as one line (JSON for structured values). */
export const previewValue = (value: unknown, max = 160): string => {
  const text =
    typeof value === 'string' ? value : (JSON.stringify(value) ?? '');
  const oneLine = text.replace(/\s+/g, ' ').trim();
  return oneLine.length > max ? `${oneLine.slice(0, max - 1)}…` : oneLine;
};

/**
 * A realistic single-item result, as the runtime persists it, for tests and
 * the hermetic editor spec. `labelPortId` / `resultsPortId` are the node's
 * output port ids so the Switch builder can read `output_ports_data`.
 */
export const sampleJevResult = (ports: {
  labelPortId: string;
  resultsPortId: string;
}): Record<string, unknown> => {
  const classification = {
    label: 'billing',
    top_label: 'billing',
    top_probability: 0.91,
    label_probabilities: { billing: 0.91, technical: 0.06, other: 0.03 },
    yes_no: { urgent: 0.22 },
    uncertain: false,
    renormalised: false,
    model: 'jev-1.14.0',
  };
  return {
    status: 'completed',
    display_modal: 'JsonDisplay',
    display_name: 'Jev classification',
    field_id: 'jev_classification',
    data: classification,
    value: classification,
    error: null,
    error_info: null,
    files: [],
    metadata: { display_type: 'json', label: 'billing' },
    output_ports_data: {
      [ports.labelPortId]: { _node_output: true, data: 'billing' },
      [ports.resultsPortId]: { _node_output: true, data: classification },
    },
  };
};
